import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { StatusDto } from 'src/app/shared/dto/status.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { StatusesService } from '../../services/statuses.service';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-statuses',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzTableModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzCheckboxModule,
        CommonModule,
        NzSelectModule,
        NzPageHeaderModule,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzDropDownModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './statuses.component.html',
    styleUrl: './statuses.component.scss'
})
export class StatusesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    statuses: StatusDto[] = [];
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    statusSelected: StatusDto = new StatusDto();
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;
    initialOrder: number = -1;
    nextStatus: StatusDto = new StatusDto();

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        abbreviation: FormControl<string>;
        isStartingStatus: FormControl<boolean>;
        isPauseStatus: FormControl<boolean>;
        isEndingStatus: FormControl<boolean>;
        nextStatus: FormControl<number | undefined>;
    }>;

    private readonly statusesService = inject(StatusesService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('statuses', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            abbreviation: ['', [Validators.required]],
            isStartingStatus: [false, [Validators.required]],
            isPauseStatus: [false, [Validators.required]],
            isEndingStatus: [false, [Validators.required]],
            nextStatus: [undefined as number | undefined]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.statusesService.setupWebSocket();
        this.statusesService.statuses$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (statuses) => {
                    this.statuses = statuses;
                    this.statusSelected = this.statuses.find(status => status.id === this.statusSelected.id) ?? new StatusDto();
                    this.nextStatus = this.statuses.find((s) => s.id === this.statusSelected.nextStatus) ?? new StatusDto();

                },
                error: (e) => {
                    this.message.create('Error cargando estados', e.message);
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.statusesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.statusSelected = new StatusDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo estado';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: StatusDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.statusSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo estado ' + this.statusSelected?.name;
        this.fieldsReadOnly = true;
        this.setData();
        this.openDrawer();
    }

    edit(data: StatusDto) {
        this.statusSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando estado ' + this.statusSelected?.name;
        this.fieldsReadOnly = false;
        this.setData();
        this.openDrawer();
    }

    setData() {
        this.nextStatus = this.statuses.find((s) => s.id === this.statusSelected.nextStatus) || new StatusDto();
    }

    cancelEditing() {
        this.closeDrawer();
    }

    async delete(statusId: number) {
        try {
            if (window.confirm('¿Estás seguro de que quieres eliminar este estado? Esta acción no se puede deshacer.')) {
                const deletedStatus: StatusDto = await this.statusesService.deleteStatus(statusId);

                const index = this.statuses.findIndex((p) => p.id === statusId);

                if (index < 0) {
                    this.message.create('error', `No se ha podido eliminar el estado ${deletedStatus.name} con id ${statusId}, estado no encontrado`);

                    return;
                }

                this.statuses.splice(index, 1);
                const data = [...this.statuses];
                this.statuses = data;

                this.message.create('success', 'Estado eliminado correctamente');
            }
        } catch (e: any) {
            this.message.create('error', `${e.message}`);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.statusSelected = new StatusDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const statusToBeProcessed: StatusDto = {
                id: this.statusSelected.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                abbreviation: this.validateForm.controls.abbreviation.value ? this.validateForm.controls.abbreviation.value : '',
                isStartingStatus: this.validateForm.controls.isStartingStatus.value ? this.validateForm.controls.isStartingStatus.value : false,
                isEndingStatus: this.validateForm.controls.isEndingStatus.value ? this.validateForm.controls.isEndingStatus.value : false,
                isPauseStatus: this.validateForm.controls.isPauseStatus.value ? this.validateForm.controls.isPauseStatus.value : false,
                nextStatus: !this.hasEndingOrPause() ? this.validateForm.controls.nextStatus.value : undefined
            };

            if (!this.isFormValid(statusToBeProcessed)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                const createdStatus: StatusDto = await this.statusesService.createStatus(statusToBeProcessed);

                this.statuses.push(createdStatus);
                const data = [...this.statuses];
                this.statuses = data;

                this.isSaving = false;
                this.message.create('success', 'Estado creado correctamente');
            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedStatus: StatusDto = await this.statusesService.updateStatus(statusToBeProcessed);

                const index = this.statuses.findIndex((r) => r.id === updatedStatus.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar el estado ${statusToBeProcessed.name} con id ${statusToBeProcessed.id}, estado no encontrado`);

                    return;
                }

                this.statuses[index] = updatedStatus;
                const data = [...this.statuses];
                this.statuses = data;

                this.isSaving = false;
                this.message.create('success', 'Estado actualizado correctamente');
            }

            this.closeDrawer();
        } catch (e: any) {
            this.isSaving = false;
            this.message.create('error', `Error: ${e.message}`);
        }
    }

    isFormValid(status: StatusDto): boolean {
        if (!status.name || status.name === '') {
            this.message.create('error', 'El nombre del estado es obligatorio');
            return false;
        }

        if (!status.abbreviation || status.abbreviation === '') {
            this.message.create('error', 'La abreviatura del estado es obligatoria');
            return false;
        }

        if (status.isStartingStatus && status.isEndingStatus) {
            this.message.create('error', 'Un estado no puede ser inicial y final a la vez');
            return false;
        }

        if (status.isStartingStatus) {
            const existingStarting = this.statuses.find((s) => s.isStartingStatus);

            if (existingStarting && existingStarting.id !== status.id) {
                this.message.create('error', 'Solo puede haber un estado inicial');
                return false;
            }
        }

        if (!status.isStartingStatus) {
            const starting = this.statuses.filter((s) => s.isStartingStatus);

            if (starting.length > 0) {
                if (starting.some((s) => s.id === status.id) && starting.length === 1) {
                    this.message.create('error', 'Debe haber al menos un estado inicial');
                    return false;
                }
            } else {
                this.message.create('error', 'Debe haber al menos un estado inicial');
                return false;
            }
        }

        if (status.isEndingStatus) {
            const existingEnding = this.statuses.find((s) => s.isEndingStatus);

            if (existingEnding && existingEnding.id !== status.id) {
                this.message.create('error', 'Solo puede haber un estado final');
                return false;
            }
        }

        if (!status.isEndingStatus) {
            const ending = this.statuses.filter((s) => s.isEndingStatus);

            if (ending.length > 0) {
                if (ending.some((s) => s.id === status.id) && ending.length === 1) {
                    this.message.create('error', 'Debe haber al menos un estado final');
                    return false;
                }
            } else {
                this.message.create('error', 'Debe haber al menos un estado final');
                return false;
            }
        }

        if (status.isPauseStatus) {
            const existingPause = this.statuses.find((s) => s.isPauseStatus);

            if (existingPause && existingPause.id !== status.id) {
                this.message.create('error', 'Solo puede haber un estado de pausa');
                return false;
            }

            if (status.isStartingStatus) {
                this.message.create('error', 'Un estado de pausa no puede ser inicial');
                return false;
            }

            if (status.isEndingStatus) {
                this.message.create('error', 'Un estado de pausa no puede ser final');
                return false;
            }
        } else {
            const pause = this.statuses.filter((s) => s.isPauseStatus);

            if (pause.length > 0) {
                if (pause.some((s) => s.id === status.id) && pause.length === 1) {
                    this.message.create('error', 'Debe haber al menos un estado de pausa');
                    return false;
                }
            } else {
                this.message.create('error', 'Debe haber al menos un estado de pausa');
                return false;
            }
        }

        if (!status.isEndingStatus && !status.nextStatus && !status.isPauseStatus) {
            this.message.create('error', 'Si no es un estado final o de pausa, debe haber un estado siguiente');
            return false;
        }

        return true;
    }

    getNextStatus(id: number | undefined): StatusDto | undefined {
        const nextStatus = this.statuses.find((s) => s.id === id);
        return nextStatus;
    }

    hasEndingOrPause(): boolean {
        return this.validateForm.controls.isEndingStatus.value || this.validateForm.controls.isPauseStatus.value;
    }
}
